import React, { useEffect, useRef, useState } from 'react'
import { useViewStore } from '../../Contexts/ViewContext'
import { useLocaleStore } from '../../Contexts/LocaleContext'
import { TextFrom } from '../../../models/enums'
import { getTextWithoutSpeechMarkdown } from '../../../utils/speechMarkdownUtils'

interface TextBubbleProps {
  id: string
  from?: TextFrom
  text?: string
  htmlText?: string
  showWaitingDots?: boolean
  isWaitingDots?: boolean
  triggerScroll: () => void
  onEnded?: () => void
  canBeEnded?: boolean
  isEnded: boolean
}

const interval = 45
const splitRegex = new RegExp(/<[^<>]+>/g)

const TextBubble = ({ from, id, text, htmlText, showWaitingDots, isWaitingDots, triggerScroll, onEnded, canBeEnded, isEnded }: TextBubbleProps): JSX.Element => {
  const isMobile = useViewStore((state) => state.isMobile)
  const isRTL = useLocaleStore((state) => state.isRTL)
  const [currentHtmlText, setCurrentHtmlText] = useState<string>('')
  const [ended, setEnded] = useState<boolean>(isEnded)

  const intervalRef = useRef<NodeJS.Timer | null>(null)
  const streamRef = useRef<Array<string>>([])
  const streamIndexRef = useRef<number>(0)
  const canBeEndedRef = useRef<boolean>()

  useEffect(() => {
    setEnded(isEnded)
  }, [isEnded])

  useEffect(() => {
    canBeEndedRef.current = canBeEnded
  }, [canBeEnded])

  useEffect(() => {
    if (htmlText) {
      isEnded ? setEnded(true) : handleNewStreaming(htmlText)
    }

    return () => {
      intervalRef.current && clearInterval(intervalRef.current)
    }
  }, [htmlText, ended])

  useEffect(() => {
    ended && endStreaming()
  }, [ended])

  const endStreaming = (): void => {
    setCurrentHtmlText(htmlText || '')
    onEnded?.()
    intervalRef.current && clearInterval(intervalRef.current)
  }

  const handleNewStreaming = (text: string): void => {
    streamRef.current = []

    let test = false
    while (test === false) {
      const index = text.search(splitRegex)
      if (index === -1) {
        streamRef.current = [...streamRef.current, ...Array.from(text)]
        test = true
      } else {
        const match = text.match(splitRegex)
        if (match?.length) {
          const firstPart = index ? Array.from(text.slice(0, index)) : []
          streamRef.current = [...streamRef.current, ...firstPart, match[0]]
          text = text.slice(firstPart.length + match[0].length)
        } else {
          test = true
        }
      }
    }

    intervalRef.current = setInterval(() => {
      if (streamIndexRef.current < streamRef.current.length) {
        const current = streamRef.current[streamIndexRef.current]
        setCurrentHtmlText((value) => `${value}${current}`)
        streamIndexRef.current = streamIndexRef.current + 1
        streamIndexRef.current % 20 === 0 && triggerScroll()
      } else {
        canBeEndedRef.current && setEnded(true)
      }
    }, interval)
  }

  return (
    <div
      id={from === TextFrom.user ? `retorik-userQuestion-${id}` : `retorik-botResponse-${id}`}
      key={id}
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`rf-w-fit ${
        from === TextFrom.user ? 'rf-min-h-fit' : 'rf-min-h-9 large-vertical:rf-min-h-10'
      } rf-px-4 rf-py-2 rf-max-w-[75%] landscape-retorik:rf-max-w-[calc((80%-10rem)*0.75)] large-vertical:rf-max-w-[45%] ${isMobile && 'rf-mx-4'} ${
        isRTL
          ? from === TextFrom.bot
            ? 'landscape-retorik:rf-mr-40 large-vertical:rf-mr-1/5 rf-self-end rf-text-textModePanelConversationBot rf-bg-textModePanelBackgroundBot rf-border rf-border-textModePanelBackgroundBot rf-rounded-tl-4'
            : 'landscape-retorik:rf-ml-1/5 large-vertical:rf-ml-1/5 rf-text-textModePanelConversationUser rf-bg-textModePanelBackgroundUser rf-border rf-border-textModePanelBackgroundUser rf-rounded-tr-4'
          : from === TextFrom.user
          ? 'landscape-retorik:rf-mr-40 large-vertical:rf-mr-1/5 rf-self-end rf-text-textModePanelConversationUser rf-bg-textModePanelBackgroundUser rf-border rf-border-textModePanelBackgroundUser rf-rounded-tl-4'
          : 'landscape-retorik:rf-ml-1/5 large-vertical:rf-ml-1/5 rf-text-textModePanelConversationBot rf-bg-textModePanelBackgroundBot rf-border rf-border-textModePanelBackgroundBot rf-rounded-tr-4'
      } rf-rounded-b-4 rf-animate-slideBottom rf-flex rf-flex-col rf-text-size-auto rf-shadow-[0_3px_6px_#00000029]`}
    >
      {isWaitingDots ? (
        <div className='rf-w-8 rf-loader-bouncing-bot' />
      ) : (
        <React.Fragment>
          {/* Text */}
          {currentHtmlText ? <div dangerouslySetInnerHTML={{ __html: currentHtmlText }} /> : text ? <div>{getTextWithoutSpeechMarkdown(text)}</div> : <React.Fragment />}

          {/* Waiting dots */}
          {showWaitingDots && <div className='rf-font-bold rf-animate-blink'>...</div>}
        </React.Fragment>
      )}
    </div>
  )
}

export default TextBubble
